import { useState } from "react"

import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/dropdown-menu"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/domains/common/components/tabs"
import { MetaData } from "@/domains/common/layout/metadata"
import { PageContainer } from "@/domains/common/layout/page-container"
import { PageTitle } from "@/domains/common/layout/page-title"
import { Body2Xl } from "@/domains/design/components/body-2xl"
import { Body3Xl } from "@/domains/design/components/body-3xl"
import { Body4Xl } from "@/domains/design/components/body-4xl"
import { Body5Xl } from "@/domains/design/components/body-5xl"
import { Body6Xl } from "@/domains/design/components/body-6xl"
import { Body7Xl } from "@/domains/design/components/body-7xl"
import { Body8Xl } from "@/domains/design/components/body-8xl"
import { BodyBase } from "@/domains/design/components/body-base"
import { BodyCaption } from "@/domains/design/components/body-caption"
import { BodyLg } from "@/domains/design/components/body-lg"
import { BodySM } from "@/domains/design/components/body-sm"
import { BodyXl } from "@/domains/design/components/body-xl"
import { BodyXs } from "@/domains/design/components/body-xs"
import { Heading1 } from "@/domains/design/components/heading1"
import { Heading2 } from "@/domains/design/components/heading2"
import { Heading3 } from "@/domains/design/components/heading3"
import { Heading4 } from "@/domains/design/components/heading4"
import { Heading5 } from "@/domains/design/components/heading5"
import { Heading6 } from "@/domains/design/components/heading6"
import { ScaleCard300 } from "@/domains/design/components/scale-card-300"
import { TypographyItem } from "@/domains/design/components/typography-item"
import { BODY_2XL_CONFIG } from "@/domains/design/system/body-2xl"
import { BODY_3XL_CONFIG } from "@/domains/design/system/body-3xl"
import { BODY_4XL_CONFIG } from "@/domains/design/system/body-4xl"
import { BODY_5XL_CONFIG } from "@/domains/design/system/body-5xl"
import { BODY_6XL_CONFIG } from "@/domains/design/system/body-6xl"
import { BODY_7XL_CONFIG } from "@/domains/design/system/body-7xl"
import { BODY_8XL_CONFIG } from "@/domains/design/system/body-8xl"
import { BODY_BASE_CONFIG } from "@/domains/design/system/body-base"
import { BODY_CAPTION_CONFIG } from "@/domains/design/system/body-caption"
import { BODY_LG_CONFIG } from "@/domains/design/system/body-lg"
import { BODY_SM_CONFIG } from "@/domains/design/system/body-sm"
import { BODY_XL_CONFIG } from "@/domains/design/system/body-xl"
import { BODY_XS_CONFIG } from "@/domains/design/system/body-xs"
import { HEADING_1_CONFIG } from "@/domains/design/system/heading-1"
import { HEADING_2_CONFIG } from "@/domains/design/system/heading-2"
import { HEADING_3_CONFIG } from "@/domains/design/system/heading-3"
import { HEADING_4_CONFIG } from "@/domains/design/system/heading-4"
import { HEADING_5_CONFIG } from "@/domains/design/system/heading-5"
import { HEADING_6_CONFIG } from "@/domains/design/system/heading-6"
import { getConfigForMode } from "@/domains/design/utils"
import { DesignSubItem } from "@/domains/navigation/subitems/design-subitem"
import { DesignTypographySubItem } from "@/domains/navigation/subitems/design-typography-subitem"
import useResponsive, { ResponsiveMode } from "@/hooks/use-responsive"

export const DESIGN_BREADCRUMB_ITEMS = [new DesignSubItem(), new DesignTypographySubItem()]

const DesignTypographyPage = () => {
  const [designMode, setDesignMode] = useState<ResponsiveMode | "responsive">("responsive")
  const { isMobile, isTablet, isDesktop } = useResponsive()

  const titleCase = (str: string) => {
    return str.charAt(0).toUpperCase() + str.slice(1)
  }

  // This is fun! we allow fixed mode or responsive mode
  let mode = designMode === "responsive" ? undefined : designMode

  // If responsive mode, we use the screen size to determine the mode
  if (designMode === "responsive") {
    if (isMobile) {
      mode = "mobile"
    } else if (isTablet) {
      mode = "tablet"
    } else if (isDesktop) {
      mode = "desktop"
    }
  }

  // and we dynamically get the config(s) for the mode
  const heading1Config = getConfigForMode(HEADING_1_CONFIG, mode)
  const heading2Config = getConfigForMode(HEADING_2_CONFIG, mode)
  const heading3Config = getConfigForMode(HEADING_3_CONFIG, mode)
  const heading4Config = getConfigForMode(HEADING_4_CONFIG, mode)
  const heading5Config = getConfigForMode(HEADING_5_CONFIG, mode)
  const heading6Config = getConfigForMode(HEADING_6_CONFIG, mode)
  const body2XlConfig = getConfigForMode(BODY_2XL_CONFIG, mode)
  const body3XlConfig = getConfigForMode(BODY_3XL_CONFIG, mode)
  const body4XlConfig = getConfigForMode(BODY_4XL_CONFIG, mode)
  const body5XlConfig = getConfigForMode(BODY_5XL_CONFIG, mode)
  const body6XlConfig = getConfigForMode(BODY_6XL_CONFIG, mode)
  const body7XlConfig = getConfigForMode(BODY_7XL_CONFIG, mode)
  const body8XlConfig = getConfigForMode(BODY_8XL_CONFIG, mode)
  const bodyXlConfig = getConfigForMode(BODY_XL_CONFIG, mode)
  const bodyLgConfig = getConfigForMode(BODY_LG_CONFIG, mode)
  const bodyBaseConfig = getConfigForMode(BODY_BASE_CONFIG, mode)
  const bodySmConfig = getConfigForMode(BODY_SM_CONFIG, mode)
  const bodyXsConfig = getConfigForMode(BODY_XS_CONFIG, mode)
  const bodyCaptionConfig = getConfigForMode(BODY_CAPTION_CONFIG, mode)

  return (
    <PageContainer>
      <MetaData title="Design | Typography" />
      <PageTitle
        title="Typography System"
        subTitle="Our typography mapped from the design system into real components"
        items={DESIGN_BREADCRUMB_ITEMS}
      />
      <Tabs defaultValue="responsive" className="w-full">
        <TabsList className="mb-8">
          <TabsTrigger value="responsive">Responsive</TabsTrigger>
          <TabsTrigger value="view-all-sizes">Size Comparison</TabsTrigger>
        </TabsList>
        <TabsContent value="responsive">
          <ScaleCard300 className="mb-8 px-8 py-4 text-t100">
            Responsive mode:{" "}
            <DropdownMenu>
              <DropdownMenuTrigger>{titleCase(designMode)}</DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem onClick={() => setDesignMode("desktop")}>Desktop</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setDesignMode("tablet")}>Tablet</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setDesignMode("mobile")}>Mobile</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setDesignMode("responsive")}>Responsive</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <div className="flex flex-col gap-4"></div>
          </ScaleCard300>
          <Heading1 mode={mode}>Headings</Heading1>
          <div className="mb-32 flex flex-col gap-4">
            <TypographyItem
              title={HEADING_1_CONFIG.title}
              fontFamily={heading1Config.family.value}
              fontWeight={heading1Config.weight.value}
              fontSize={heading1Config.size.px}
              lineHeight={heading1Config.lineHeight.px}
              letterSpacing="0px"
              example={<Heading1 mode={mode}>AaBbCc</Heading1>}
            />
            <TypographyItem
              title={HEADING_2_CONFIG.title}
              fontFamily={heading2Config.family.value}
              fontWeight={heading2Config.weight.value}
              fontSize={heading2Config.size.px}
              lineHeight={heading2Config.lineHeight.px}
              letterSpacing="0px"
              example={<Heading2 mode={mode}>AaBbCc</Heading2>}
            />
            <TypographyItem
              title={HEADING_3_CONFIG.title}
              fontFamily={heading3Config.family.value}
              fontWeight={heading3Config.weight.value}
              fontSize={heading3Config.size.px}
              lineHeight={heading3Config.lineHeight.px}
              letterSpacing="0px"
              example={<Heading3 mode={mode}>AaBbCc</Heading3>}
            />
            <TypographyItem
              title={HEADING_4_CONFIG.title}
              fontFamily={heading4Config.family.value}
              fontWeight={heading4Config.weight.value}
              fontSize={heading4Config.size.px}
              lineHeight={heading4Config.lineHeight.px}
              letterSpacing="0px"
              example={<Heading4 mode={mode}>AaBbCc</Heading4>}
            />
            <TypographyItem
              title={HEADING_5_CONFIG.title}
              fontFamily={heading5Config.family.value}
              fontWeight={heading5Config.weight.value}
              fontSize={heading5Config.size.px}
              lineHeight={heading5Config.lineHeight.px}
              letterSpacing="0px"
              example={<Heading5 mode={mode}>AaBbCc</Heading5>}
            />
            <TypographyItem
              title={HEADING_6_CONFIG.title}
              fontFamily={heading6Config.family.value}
              fontWeight={heading6Config.weight.value}
              fontSize={heading6Config.size.px}
              lineHeight={heading6Config.lineHeight.px}
              letterSpacing="0px"
              example={<Heading6 mode={mode}>AaBbCc</Heading6>}
            />
          </div>
          <Heading1 mode={mode}>Body Text</Heading1>
          <div className="mb-32 flex flex-col gap-4">
            <TypographyItem
              title={BODY_8XL_CONFIG.title}
              fontFamily={body8XlConfig.family.value}
              fontSize={body2XlConfig.size.px}
              lineHeight={body8XlConfig.lineHeight.px}
              letterSpacing="0px"
              example={<Body8Xl>AaBbCc</Body8Xl>}
            />
            <TypographyItem
              title={BODY_7XL_CONFIG.title}
              fontFamily={body7XlConfig.family.value}
              fontSize={body7XlConfig.size.px}
              lineHeight={body7XlConfig.lineHeight.px}
              letterSpacing="0px"
              example={<Body7Xl>AaBbCc</Body7Xl>}
            />
            <TypographyItem
              title={BODY_6XL_CONFIG.title}
              fontFamily={body6XlConfig.family.value}
              fontSize={body6XlConfig.size.px}
              lineHeight={body6XlConfig.lineHeight.px}
              letterSpacing="0px"
              example={<Body6Xl>AaBbCc</Body6Xl>}
            />
            <TypographyItem
              title={BODY_5XL_CONFIG.title}
              fontFamily={body5XlConfig.family.value}
              fontSize={body5XlConfig.size.px}
              lineHeight={body5XlConfig.lineHeight.px}
              letterSpacing="0px"
              example={<Body5Xl>AaBbCc</Body5Xl>}
            />
            <TypographyItem
              title={BODY_4XL_CONFIG.title}
              fontFamily={body4XlConfig.family.value}
              fontSize={body4XlConfig.size.px}
              lineHeight={body4XlConfig.lineHeight.px}
              letterSpacing="0px"
              example={<Body4Xl>AaBbCc</Body4Xl>}
            />
            <TypographyItem
              title={BODY_3XL_CONFIG.title}
              fontFamily={body3XlConfig.family.value}
              fontSize={body3XlConfig.size.px}
              lineHeight={body3XlConfig.lineHeight.px}
              letterSpacing="0px"
              example={<Body3Xl>AaBbCc</Body3Xl>}
            />
            <TypographyItem
              title={BODY_2XL_CONFIG.title}
              fontFamily={body2XlConfig.family.value}
              fontSize={body2XlConfig.size.px}
              lineHeight={body2XlConfig.lineHeight.px}
              letterSpacing="0px"
              example={<Body2Xl>AaBbCc</Body2Xl>}
            />
            <TypographyItem
              title={BODY_XL_CONFIG.title}
              fontFamily={bodyXlConfig.family.value}
              fontSize={bodyXlConfig.size.px}
              lineHeight={bodyXlConfig.lineHeight.px}
              letterSpacing="0px"
              example={<BodyXl mode={mode}>AaBbCc</BodyXl>}
            />
            <TypographyItem
              title={BODY_LG_CONFIG.title}
              fontFamily={bodyLgConfig.family.value}
              fontSize={bodyLgConfig.size.px}
              lineHeight={bodyLgConfig.lineHeight.px}
              letterSpacing="0px"
              example={<BodyLg mode={mode}>AaBbCc</BodyLg>}
            />
            <TypographyItem
              title={BODY_BASE_CONFIG.title}
              fontFamily={bodyBaseConfig.family.value}
              fontSize={bodyBaseConfig.size.px}
              lineHeight={bodyBaseConfig.lineHeight.px}
              letterSpacing="0px"
              example={<BodyBase mode={mode}>AaBbCc</BodyBase>}
            />
            <TypographyItem
              title={BODY_SM_CONFIG.title}
              fontFamily={bodySmConfig.family.value}
              fontSize={bodySmConfig.size.px}
              lineHeight={bodySmConfig.lineHeight.px}
              letterSpacing="0px"
              example={<BodySM mode={mode}>AaBbCc</BodySM>}
            />
            <TypographyItem
              title={BODY_XS_CONFIG.title}
              fontFamily={bodyXsConfig.family.value}
              fontSize={bodyXsConfig.size.px}
              lineHeight={bodyXsConfig.lineHeight.px}
              letterSpacing="0px"
              example={<BodyXs mode={mode}>AaBbCc</BodyXs>}
            />
            <TypographyItem
              title={BODY_CAPTION_CONFIG.title}
              fontFamily={bodyCaptionConfig.family.value}
              fontSize={bodyCaptionConfig.size.px}
              lineHeight={bodyCaptionConfig.lineHeight.px}
              letterSpacing="0px"
              example={<BodyCaption mode={mode}>AaBbCc</BodyCaption>}
            />
          </div>
        </TabsContent>
        <TabsContent value="view-all-sizes">
          <div className="flex flex-col gap-6">
            <div className="grid grid-cols-3 gap-8">
              <div className="text-lg font-semibold text-t100">Desktop</div>
              <div className="text-lg font-semibold text-t100">Tablet</div>
              <div className="text-lg font-semibold text-t100">Mobile</div>
            </div>
            <div className="grid grid-cols-3 gap-8">
              <div className="flex flex-col gap-2">
                <BodyCaption>headingH1 desktop</BodyCaption>
                <Heading1 mode="desktop">AaBbCc</Heading1>
              </div>
              <div className="flex flex-col gap-2">
                <BodyCaption>headingH1 tablet</BodyCaption>
                <Heading1 mode="tablet">AaBbCc</Heading1>
              </div>
              <div className="flex flex-col gap-2">
                <BodyCaption>headingH1 mobile</BodyCaption>
                <Heading1 mode="mobile">AaBbCc</Heading1>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-8">
              <div className="flex flex-col gap-2">
                <BodyCaption>headingH2 desktop</BodyCaption>
                <Heading2 mode="desktop">AaBbCc</Heading2>
              </div>
              <div className="flex flex-col gap-2">
                <BodyCaption>headingH2 tablet</BodyCaption>
                <Heading2 mode="tablet">AaBbCc</Heading2>
              </div>
              <div className="flex flex-col gap-2">
                <BodyCaption>headingH2 mobile</BodyCaption>
                <Heading2 mode="mobile">AaBbCc</Heading2>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-8">
              <div className="flex flex-col gap-2">
                <BodyCaption>headingH3 desktop</BodyCaption>
                <Heading3 mode="desktop">AaBbCc</Heading3>
              </div>
              <div className="flex flex-col gap-2">
                <BodyCaption>headingH3 tablet</BodyCaption>
                <Heading3 mode="tablet">AaBbCc</Heading3>
              </div>
              <div className="flex flex-col gap-2">
                <BodyCaption>headingH3 mobile</BodyCaption>
                <Heading3 mode="mobile">AaBbCc</Heading3>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-8">
              <div className="flex flex-col gap-2">
                <BodyCaption>headingH4 desktop</BodyCaption>
                <Heading4 mode="desktop">AaBbCc</Heading4>
              </div>
              <div className="flex flex-col gap-2">
                <BodyCaption>headingH4 tablet</BodyCaption>
                <Heading4 mode="tablet">AaBbCc</Heading4>
              </div>
              <div className="flex flex-col gap-2">
                <BodyCaption>headingH4 mobile</BodyCaption>
                <Heading4 mode="mobile">AaBbCc</Heading4>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-8">
              <div className="flex flex-col gap-2">
                <BodyCaption>headingH5 desktop</BodyCaption>
                <Heading5 mode="desktop">AaBbCc</Heading5>
              </div>
              <div className="flex flex-col gap-2">
                <BodyCaption>headingH5 tablet</BodyCaption>
                <Heading5 mode="tablet">AaBbCc</Heading5>
              </div>
              <div className="flex flex-col gap-2">
                <BodyCaption>headingH5 mobile</BodyCaption>
                <Heading5 mode="mobile">AaBbCc</Heading5>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-8">
              <div className="flex flex-col gap-2">
                <BodyCaption>headingH6 desktop</BodyCaption>
                <Heading6 mode="desktop">AaBbCc</Heading6>
              </div>
              <div className="flex flex-col gap-2">
                <BodyCaption>headingH6 tablet</BodyCaption>
                <Heading6 mode="tablet">AaBbCc</Heading6>
              </div>
              <div className="flex flex-col gap-2">
                <BodyCaption>headingH6 mobile</BodyCaption>
                <Heading6 mode="mobile">AaBbCc</Heading6>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-8">
              <div className="flex flex-col gap-2">
                <BodyCaption>body8Xl desktop</BodyCaption>
                <Body8Xl mode="desktop">AaBbCc</Body8Xl>
              </div>
              <div className="flex flex-col gap-2">
                <BodyCaption>body8Xl tablet</BodyCaption>
                <Body8Xl mode="tablet">AaBbCc</Body8Xl>
              </div>
              <div className="flex flex-col gap-2">
                <BodyCaption>body8Xl mobile</BodyCaption>
                <Body8Xl mode="mobile">AaBbCc</Body8Xl>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-8">
              <div className="flex flex-col gap-2">
                <BodyCaption>body7Xl desktop</BodyCaption>
                <Body7Xl mode="desktop">AaBbCc</Body7Xl>
              </div>
              <div className="flex flex-col gap-2">
                <BodyCaption>body7Xl tablet</BodyCaption>
                <Body7Xl mode="tablet">AaBbCc</Body7Xl>
              </div>
              <div className="flex flex-col gap-2">
                <BodyCaption>body7Xl mobile</BodyCaption>
                <Body7Xl mode="mobile">AaBbCc</Body7Xl>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-8">
              <div className="flex flex-col gap-2">
                <BodyCaption>body6Xl desktop</BodyCaption>
                <Body6Xl mode="desktop">AaBbCc</Body6Xl>
              </div>
              <div className="flex flex-col gap-2">
                <BodyCaption>body6Xl tablet</BodyCaption>
                <Body6Xl mode="tablet">AaBbCc</Body6Xl>
              </div>
              <div className="flex flex-col gap-2">
                <BodyCaption>body6Xl mobile</BodyCaption>
                <Body6Xl mode="mobile">AaBbCc</Body6Xl>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-8">
              <div className="flex flex-col gap-2">
                <BodyCaption>body5Xl desktop</BodyCaption>
                <Body5Xl mode="desktop">AaBbCc</Body5Xl>
              </div>
              <div className="flex flex-col gap-2">
                <BodyCaption>body5Xl tablet</BodyCaption>
                <Body5Xl mode="tablet">AaBbCc</Body5Xl>
              </div>
              <div className="flex flex-col gap-2">
                <BodyCaption>body5Xl mobile</BodyCaption>
                <Body5Xl mode="mobile">AaBbCc</Body5Xl>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-8">
              <div className="flex flex-col gap-2">
                <BodyCaption>body4Xl desktop</BodyCaption>
                <Body4Xl mode="desktop">AaBbCc</Body4Xl>
              </div>
              <div className="flex flex-col gap-2">
                <BodyCaption>body4Xl tablet</BodyCaption>
                <Body4Xl mode="tablet">AaBbCc</Body4Xl>
              </div>
              <div className="flex flex-col gap-2">
                <BodyCaption>body4Xl mobile</BodyCaption>
                <Body4Xl mode="mobile">AaBbCc</Body4Xl>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-8">
              <div className="flex flex-col gap-2">
                <BodyCaption>body3Xl desktop</BodyCaption>
                <Body3Xl mode="desktop">AaBbCc</Body3Xl>
              </div>
              <div className="flex flex-col gap-2">
                <BodyCaption>body3Xl tablet</BodyCaption>
                <Body3Xl mode="tablet">AaBbCc</Body3Xl>
              </div>
              <div className="flex flex-col gap-2">
                <BodyCaption>body3Xl mobile</BodyCaption>
                <Body3Xl mode="mobile">AaBbCc</Body3Xl>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-8">
              <div className="flex flex-col gap-2">
                <BodyCaption>body2Xl desktop</BodyCaption>
                <Body2Xl mode="desktop">AaBbCc</Body2Xl>
              </div>
              <div className="flex flex-col gap-2">
                <BodyCaption>body2Xl tablet</BodyCaption>
                <Body2Xl mode="tablet">AaBbCc</Body2Xl>
              </div>
              <div className="flex flex-col gap-2">
                <BodyCaption>body2Xl mobile</BodyCaption>
                <Body2Xl mode="mobile">AaBbCc</Body2Xl>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-8">
              <div className="flex flex-col gap-2">
                <BodyCaption>bodyXl desktop</BodyCaption>
                <BodyXl mode="desktop">AaBbCc</BodyXl>
              </div>
              <div className="flex flex-col gap-2">
                <BodyCaption>bodyXl tablet</BodyCaption>
                <BodyXl mode="tablet">AaBbCc</BodyXl>
              </div>
              <div className="flex flex-col gap-2">
                <BodyCaption>bodyXl mobile</BodyCaption>
                <BodyXl mode="mobile">AaBbCc</BodyXl>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-8">
              <div className="flex flex-col gap-2">
                <BodyCaption>bodyLg desktop</BodyCaption>
                <BodyLg mode="desktop">AaBbCc</BodyLg>
              </div>
              <div className="flex flex-col gap-2">
                <BodyCaption>bodyLg tablet</BodyCaption>
                <BodyLg mode="tablet">AaBbCc</BodyLg>
              </div>
              <div className="flex flex-col gap-2">
                <BodyCaption>bodyLg mobile</BodyCaption>
                <BodyLg mode="mobile">AaBbCc</BodyLg>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-8">
              <div className="flex flex-col gap-2">
                <BodyCaption>bodyBase desktop</BodyCaption>
                <BodyBase mode="desktop">AaBbCc</BodyBase>
              </div>
              <div className="flex flex-col gap-2">
                <BodyCaption>bodyBase tablet</BodyCaption>
                <BodyBase mode="tablet">AaBbCc</BodyBase>
              </div>
              <div className="flex flex-col gap-2">
                <BodyCaption>bodyBase mobile</BodyCaption>
                <BodyBase mode="mobile">AaBbCc</BodyBase>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-8">
              <div className="flex flex-col gap-2">
                <BodyCaption>bodySM desktop</BodyCaption>
                <BodySM mode="desktop">AaBbCc</BodySM>
              </div>
              <div className="flex flex-col gap-2">
                <BodyCaption>bodySM tablet</BodyCaption>
                <BodySM mode="tablet">AaBbCc</BodySM>
              </div>
              <div className="flex flex-col gap-2">
                <BodyCaption>bodySM mobile</BodyCaption>
                <BodySM mode="mobile">AaBbCc</BodySM>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-8">
              <div className="flex flex-col gap-2">
                <BodyCaption>bodyXs desktop</BodyCaption>
                <BodyXs mode="desktop">AaBbCc</BodyXs>
              </div>
              <div className="flex flex-col gap-2">
                <BodyCaption>bodyXs tablet</BodyCaption>
                <BodyXs mode="tablet">AaBbCc</BodyXs>
              </div>
              <div className="flex flex-col gap-2">
                <BodyCaption>bodyXs mobile</BodyCaption>
                <BodyXs mode="mobile">AaBbCc</BodyXs>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-8">
              <div className="flex flex-col gap-2">
                <BodyCaption>bodyCaption desktop</BodyCaption>
                <BodyCaption mode="desktop">AaBbCc</BodyCaption>
              </div>
              <div className="flex flex-col gap-2">
                <BodyCaption>bodyCaption tablet</BodyCaption>
                <BodyCaption mode="tablet">AaBbCc</BodyCaption>
              </div>
              <div className="flex flex-col gap-2">
                <BodyCaption>bodyCaption mobile</BodyCaption>
                <BodyCaption mode="mobile">AaBbCc</BodyCaption>
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </PageContainer>
  )
}

export default DesignTypographyPage
